import { useNavigate } from 'react-router-dom';
import { Form, Input, Modal, Radio, notification } from 'antd';
import {
  type StartingPoint,
  useCreateTemplateMutation,
  useCreateWorksheetMutation,
} from '@/state/slices/reportTemplates';
import styles from './CreateTemplateModal.module.css';

export type TemplateKind = 'worksheet' | 'report';

const COPY: Record<
  TemplateKind,
  { title: string; standard: string; blank: string; editorPath: string }
> = {
  worksheet: {
    title: 'New Worksheet Template',
    standard: 'Begin with the iCardio.ai Standard Worksheet',
    blank: 'Create a Blank Worksheet',
    editorPath: '/templates/worksheets',
  },
  report: {
    title: 'New Report Template',
    standard: 'Begin with the iCardio.ai Standard Report',
    blank: 'Create a Blank Report',
    editorPath: '/templates/templates',
  },
};

interface FormValues {
  name: string;
  startingPoint: StartingPoint;
}

interface CreateTemplateModalProps {
  kind: TemplateKind;
  open: boolean;
  onClose: () => void;
}

/** Name the new template and pick what it starts from, then open its editor. */
export function CreateTemplateModal({
  kind,
  open,
  onClose,
}: CreateTemplateModalProps) {
  const copy = COPY[kind];
  const navigate = useNavigate();
  const [form] = Form.useForm<FormValues>();
  const worksheetMutation = useCreateWorksheetMutation();
  const templateMutation = useCreateTemplateMutation();
  const [create, { isLoading }] =
    kind === 'worksheet' ? worksheetMutation : templateMutation;

  // Reset on every close, so the next open starts empty with the standard picked.
  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      const created = await create(values).unwrap();
      handleClose();
      navigate(`${copy.editorPath}/${created.uuid}`);
    } catch (err) {
      const nameError = (err as { data?: { name?: string[] } })?.data?.name;
      if (nameError) {
        form.setFields([{ name: 'name', errors: nameError }]);
      } else {
        notification.error({ title: 'Failed to create template' });
      }
    }
  };

  return (
    <Modal
      title={copy.title}
      open={open}
      okText="Create"
      onOk={() => form.submit()}
      confirmLoading={isLoading}
      onCancel={handleClose}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ startingPoint: 'STANDARD' }}
        onFinish={handleSubmit}
      >
        <Form.Item
          label="Template name"
          name="name"
          rules={[
            { required: true, whitespace: true, message: 'Enter a name' },
          ]}
        >
          <Input autoFocus maxLength={100} />
        </Form.Item>
        <Form.Item label="Starting configuration" name="startingPoint">
          <Radio.Group className={styles.options}>
            <Radio value="STANDARD">{copy.standard}</Radio>
            <Radio value="BLANK">{copy.blank}</Radio>
          </Radio.Group>
        </Form.Item>
      </Form>
    </Modal>
  );
}
